import { StandardEvents } from '@shopify/events';

/**
 * Wedwyn personalization form (blocks/wedwyn-personalization.liquid; docs/27 CMP-25, CMP-32a).
 * - Native constraints (required / maxlength) block the add. This element cancels the browser bubbles ('invalid', capture)
 *   and shows an error at each field + a summary at the top of the form, focused after an attempt (ATC-S2).
 *   The Add to cart button is never disabled here (ATC rule: only adding / unavailable lock it).
 * - Fields hidden by show_when or by "send the rest later" are disabled: not required, not checked, not sent; values stay.
 * - Dates: the printed text follows the format the customer picks (paired "<key>_display" list); ISO goes in _<key>_iso.
 * Validity is read with control.validity (never checkValidity / reportValidity, which would fire 'invalid' again).
 */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Formats an ISO date (yyyy-mm-dd) the way the example option is written. */
export function formatDate(iso, example) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return '';
  const [, y, mo, d] = m;
  const month = MONTHS[Number(mo) - 1];
  if (/^[A-Za-z]+ \d{1,2}, \d{4}$/.test(example)) return `${month} ${Number(d)}, ${y}`;
  if (/^\d{1,2} [A-Za-z]+ \d{4}$/.test(example)) return `${Number(d)} ${month} ${y}`;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(example)) return `${d}.${mo}.${y}`;
  return iso;
}

class WedwynPersonalization extends HTMLElement {
  #attempted = false;
  #scheduled = false;

  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    this.section = this.closest('.shopify-section, dialog');
    this.optionNames = JSON.parse(this.dataset.optionNames || '[]');
    for (const field of this.fields) {
      if (field.hidden && field.dataset.showWhen) field.dataset.offVariant = '1';
    }
    this.addEventListener('input', this.#onEdit);
    this.addEventListener('change', this.#onEdit);
    this.addEventListener('focusout', this.#onBlur);
    this.addEventListener('click', this.#onSummaryClick);
    document.addEventListener('invalid', this.#onInvalid, true);
    this.form?.addEventListener('formdata', this.#onFormData);
    this.section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.querySelectorAll('[data-date-key]').forEach((input) => this.#syncDate(input.dataset.dateKey));
    this.querySelectorAll('[data-count-for]').forEach((count) => this.#syncCount(document.getElementById(count.dataset.countFor)));
    this.#syncHint();
  }

  disconnectedCallback() {
    document.removeEventListener('invalid', this.#onInvalid, true);
    this.form?.removeEventListener('formdata', this.#onFormData);
    this.section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /**
   * An empty field sends nothing (WED-PM-113): drop every empty line item property — optional fields left blank, the
   * ISO / printed date of an empty date — when the add-to-cart FormData is built (fires on `new FormData(form)`).
   * @param {FormDataEvent} event
   */
  #onFormData = (event) => {
    for (const [name, value] of [...event.formData.entries()]) {
      if (name.startsWith('properties[') && value === '') event.formData.delete(name);
    }
  };

  get fields() {
    return [...this.querySelectorAll('.wedwyn-perso__field')];
  }

  #ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  #controls(root = this) {
    return [...root.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]), select, textarea')].filter((c) => c.closest('.wedwyn-perso__field'));
  }

  #active() {
    return this.#controls().filter((c) => !c.disabled);
  }

  #label(control) {
    return control.closest('.wedwyn-perso__field')?.dataset.label || '';
  }

  #message(control) {
    const label = this.#label(control);
    const lower = label.charAt(0).toLowerCase() + label.slice(1);
    if (control.dataset.formatFor) {
      const date = this.querySelector(`[data-date-key="${control.dataset.formatFor}"]`);
      if (date?.value && control.dataset.ownRequired !== 'true') return this.dataset.msgFormat;
    }
    const template = control.tagName === 'SELECT' ? this.dataset.msgChoose : this.dataset.msgRequired;
    return template.replace('[label]', lower);
  }

  /** Shows or clears the error of one control. Returns true when it is invalid. */
  #showError(control) {
    const error = document.getElementById(`${control.id}-error`);
    const invalid = !control.disabled && !control.validity.valid;
    control.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    if (error) {
      error.textContent = invalid ? this.#message(control) : '';
      error.hidden = !invalid;
    }
    return invalid;
  }

  #renderSummary(focus) {
    const summary = this.#ref('summary');
    if (!summary) return;
    const invalid = this.#active().filter((c) => this.#showError(c));
    if (!invalid.length) {
      summary.hidden = true;
      return;
    }
    // WED-PM-168 #1: singular message for one field ("1 detail"), plural for 2+
    const msg = invalid.length === 1 && this.dataset.msgSummaryOne ? this.dataset.msgSummaryOne : this.dataset.msgSummary;
    summary.querySelector('.wedwyn-perso__summary-title').textContent = msg.replace('[count]', String(invalid.length));
    const list = summary.querySelector('.wedwyn-perso__summary-list');
    list.replaceChildren(
      ...invalid.map((c) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${c.id}`;
        a.textContent = this.#message(c);
        li.append(a);
        return li;
      })
    );
    summary.hidden = false;
    if (focus) {
      summary.focus({ preventScroll: true });
      summary.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  #onInvalid = (event) => {
    const control = event.target;
    if (!(control instanceof HTMLElement) || !this.contains(control)) return;
    event.preventDefault();
    if (this.#scheduled) return;
    this.#scheduled = true;
    setTimeout(() => {
      this.#scheduled = false;
      this.#attempted = true;
      this.#renderSummary(true);
    }, 0);
  };

  #onSummaryClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('.wedwyn-perso__summary-list a') : null;
    if (!link) return;
    event.preventDefault();
    const control = document.getElementById(link.getAttribute('href').slice(1));
    control?.focus();
    control?.scrollIntoView({ block: 'center' });
  };

  #onEdit = (event) => {
    const target = event.target;
    if (target === this.#ref('sendLater')) {
      if (event.type === 'change') this.#toggleSendLater(target.checked);
      return;
    }
    if (!(target instanceof HTMLElement)) return;
    target.dataset.touched = 'true';
    if (target.dataset.dateKey) this.#syncDate(target.dataset.dateKey);
    if (target.dataset.formatFor) this.#syncDate(target.dataset.formatFor);
    this.#syncCount(target);
    this.#syncHint();
    if (this.#attempted) this.#renderSummary(false);
    else if (target.getAttribute('aria-invalid') === 'true') this.#showError(target);
  };

  #onBlur = (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && target.dataset.touched === 'true' && this.#controls().includes(target)) {
      if (this.#attempted) this.#renderSummary(false);
      else this.#showError(target);
    }
  };

  #syncCount(control) {
    if (!control) return;
    const count = this.querySelector(`[data-count-for="${control.id}"]`);
    if (count) count.textContent = `${control.value.length} / ${count.dataset.max}`;
  }

  #syncDate(key) {
    const date = this.querySelector(`[data-date-key="${key}"]`);
    const format = this.querySelector(`[data-format-for="${key}"]`);
    const printed = this.querySelector(`[data-printed-for="${key}"]`);
    if (!date) return;
    if (format && format.dataset.ownRequired !== 'true') format.required = Boolean(date.value);
    if (printed) printed.value = format ? (format.value ? formatDate(date.value, format.value) : '') : date.value;
  }

  #syncHint() {
    const hint = this.#ref('hint');
    if (hint) hint.hidden = !this.#active().some((c) => c.validity.valueMissing);
  }

  /** Applies hidden / disabled to a field from its two switches; returns true when it changed. */
  #apply(field) {
    const off = field.dataset.offVariant === '1' || field.dataset.offLater === '1';
    if (field.hidden === off) return false;
    field.hidden = off;
    field.querySelectorAll('input, select, textarea').forEach((c) => {
      c.disabled = off;
    });
    if (off) field.querySelectorAll('select, textarea, input:not([type="hidden"])').forEach((c) => this.#showError(c));
    return true;
  }

  #announce(added, removed) {
    const parts = [];
    if (added.length) parts.push(this.dataset.msgAdded.replace('[fields]', added.join(', ')));
    if (removed.length) parts.push(this.dataset.msgRemoved.replace('[fields]', removed.join(', ')));
    const live = this.#ref('live');
    if (live && parts.length) live.textContent = parts.join('. ');
  }

  #update(mutate) {
    const added = [];
    const removed = [];
    for (const field of this.fields) {
      const wasHidden = field.hidden;
      mutate(field);
      if (this.#apply(field)) (wasHidden ? added : removed).push(field.dataset.label);
    }
    this.querySelectorAll('[data-date-key]').forEach((input) => this.#syncDate(input.dataset.dateKey));
    this.#announce(added, removed);
    this.#syncHint();
    if (this.#attempted) this.#renderSummary(false);
  }

  #toggleSendLater(on) {
    this.querySelectorAll('[data-later-flag]').forEach((input) => {
      input.disabled = !on;
    });
    const note = this.#ref('sendLaterNote');
    if (note) note.hidden = !on;
    this.#update((field) => {
      if (field.hasAttribute('data-deferrable')) field.dataset.offLater = on ? '1' : '';
    });
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      .then(({ detail }) => {
        const options = detail?.resource?.options;
        if (!Array.isArray(options)) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;
        const selected = this.optionNames.map((name, i) => `${name}=${options[i]}`);
        this.#update((field) => {
          if (!field.dataset.showWhen) return;
          const match = JSON.parse(field.dataset.showWhen).some((cond) => selected.includes(cond));
          field.dataset.offVariant = match ? '' : '1';
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[wedwyn-personalization] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('wedwyn-personalization')) {
  customElements.define('wedwyn-personalization', WedwynPersonalization);
}
